import { FunctionComponent, useEffect, useState } from 'react';
import { ProductivityDetailTable } from './ProductivityDetailTable';
import { useDispatch } from 'react-redux';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import styles from './Productivity.module.scss';

interface ProductivityProps {
    description?: string;
}

interface ProductivityData {
    staff: string;
    totalAppointments: number;
    totalPets: number;
    avgDuration: number;
    hoursScheduled: number;
    availability: number;
    utilization: number;
}

export const Productivity: FunctionComponent<ProductivityProps> = props => {
    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone).startOf('month'),
        to: moment().tz(timeZone).endOf('month')
    });

    const [productivityData, setProductivityData] = useState<ProductivityData[] | null>(null);
    const [loading, setLoading] = useState(false);

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    // Mock data for productivity
    const mockData: ProductivityData[] = [
        {
            staff: 'Sarah Johnson',
            totalAppointments: 45,
            totalPets: 38,
            avgDuration: 75,
            hoursScheduled: 40,
            availability: 95.0,
            utilization: 88.5
        },
        {
            staff: 'Mike Chen',
            totalAppointments: 38,
            totalPets: 32,
            avgDuration: 65,
            hoursScheduled: 35,
            availability: 92.0,
            utilization: 85.2
        },
        {
            staff: 'Lisa Rodriguez',
            totalAppointments: 42,
            totalPets: 35,
            avgDuration: 70,
            hoursScheduled: 38,
            availability: 94.5,
            utilization: 87.1
        },
        {
            staff: 'David Wilson',
            totalAppointments: 25,
            totalPets: 22,
            avgDuration: 60,
            hoursScheduled: 30,
            availability: 90.0,
            utilization: 82.3
        }
    ];

    // export
    const productivityCsvHeaders = [
        'Staff',
        'Total Appointments',
        'Total Pets',
        'Avg Duration (min)',
        'Hours Scheduled',
        'Availability (%)',
        'Utilization (%)'
    ];

    const dataToExport =
        productivityData?.map(item => [
            item.staff,
            item.totalAppointments.toString(),
            item.totalPets.toString(),
            `${item.avgDuration} min`,
            item.hoursScheduled.toString(),
            `${item.availability.toFixed(1)}%`,
            `${item.utilization.toFixed(1)}%`
        ]) || [];

    const exportFile = {
        name: `Productivity - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: dataToExport,
        headers: productivityCsvHeaders
    };

    const calendar = {
        optionSelected: 'This month'
    };

    const [retryAttempt, setRetryAttempt] = useState(0);

    useEffect(() => {
        // Simulate API call
        setLoading(true);
        setTimeout(() => {
            setProductivityData(mockData);
            setLoading(false);
        }, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    return (
        <div
            className={styles.container}
            key={retryAttempt}
        >
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>
            <div className={styles.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                />
            </div>
            <ProductivityDetailTable
                loading={loading}
                data={productivityData}
            />
        </div>
    );
};

export default Productivity;
